import { useEffect, useRef } from "react";
import { useAuthStore } from "../store/authStore";
import { useSettingsStore } from "../store/tenantStore";
import { apiFetch } from "../services/api";
import { API_ENDPOINTS } from "../config/api-endpoints";
import { COLOR_PRESETS } from "../config/themePresets";

interface BackendPrefs {
  colorTheme?: string | null;
  theme?: string | null;
  fontSize?: string | null;
}

/**
 * Fetches appearance preferences (colorTheme, theme, fontSize) from the backend
 * once per session when the user is authenticated, then syncs them into the
 * Zustand settings store.
 *
 * This makes the backend the source of truth so the theme survives across
 * browsers and page refreshes — even if localStorage is cleared.
 *
 * Only non-null values from the backend are applied (null means the user has
 * never explicitly saved that setting via the Settings page, so we leave the
 * local/default value untouched).
 */
export function useBackendPreferences() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const updateSettings = useSettingsStore((s) => s.updateSettings);
  const hasSynced = useRef(false);

  const isValidColorTheme = (value?: string | null) =>
    !!value && COLOR_PRESETS.some((preset) => preset.id === value);

  useEffect(() => {
    // Reset sync flag when user logs out so the next login fetches fresh prefs
    if (!isAuthenticated) {
      hasSynced.current = false;
      return;
    }

    // Only sync once per session (not on every re-render)
    if (hasSynced.current) return;
    hasSynced.current = true;

    apiFetch<{ data: BackendPrefs }>(API_ENDPOINTS.USERS.GET_PREFERENCES)
      .then((res) => {
        const prefs = res?.data;
        if (!prefs) return;

        const patch: Partial<{ colorTheme: string; theme: string; fontSize: string }> = {};

        // Only apply values that were explicitly saved by the user (non-null)
        if (isValidColorTheme(prefs.colorTheme)) patch.colorTheme = prefs.colorTheme as string;
        if (prefs.theme != null) patch.theme = prefs.theme;
        if (prefs.fontSize != null) patch.fontSize = prefs.fontSize;

        if (Object.keys(patch).length > 0) {
          updateSettings(patch as Partial<import("../types").UserSettings>);
        }
      })
      .catch(() => {
        // Silently ignore network errors — localStorage fallback remains active
      });
  }, [isAuthenticated, updateSettings]);
}
